import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { money, formatTime } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Trash2, Printer } from "lucide-react";

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang, localized } = useI18n();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [method, setMethod] = useState("cash");
  const [tip, setTip] = useState(0);

  const load = async () => {
    const [{ data }, m] = await Promise.all([api.get(`/orders/${id}`), api.get("/menu-items")]);
    setOrder(data);
    setItems(m.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const addItem = async (menu_item_id) => {
    try {
      await api.post(`/orders/${id}/items`, { menu_item_id, qty: 1 });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const removeItem = async (item_id) => {
    await api.delete(`/orders/${id}/items/${item_id}`);
    await load();
  };
  const pay = async () => {
    try {
      await api.post(`/orders/${id}/pay`, { method, tip: parseFloat(tip) || 0 });
      setPayOpen(false);
      toast.success(t("payment_success"));
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (!order) return <div className="p-6 text-sm text-muted-foreground">{t("loading")}</div>;
  const paid = order.status === "paid";

  return (
    <div className="p-4 md:p-8 space-y-6">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-display font-bold text-2xl">
              #{order.id.slice(0, 6)} · <span className="font-mono text-primary">{money(order.total)}</span>
            </h1>
            <div className="text-xs text-muted-foreground font-mono">
              {formatTime(order.created_at)} · <Badge variant={paid ? "secondary" : "default"} className="ml-1">{t(`status_${order.status}`)}</Badge>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} className="hidden md:inline-flex">
            <Printer className="w-4 h-4 mr-2" /> {lang === "tr" ? "Yazdır" : "Drucken"}
          </Button>
          {!paid && (
            <Button onClick={() => setPayOpen(true)} data-testid="open-pay-btn" className="active:scale-95">
              {t("pay")}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Items */}
        <Card className="border shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display">{lang === "tr" ? "Ürünler" : "Artikel"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {order.items.map((it) => (
              <div key={it.id} className="flex justify-between items-center py-1 border-b last:border-b-0">
                <div>
                  <div className="text-sm">{it.name_snapshot}</div>
                  <div className="text-xs text-muted-foreground font-mono">{it.qty}× · {money(it.price)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="font-mono font-semibold">{money(it.qty * it.price)}</div>
                  {!paid && (
                    <Button size="icon" variant="ghost" onClick={() => removeItem(it.id)} data-testid={`remove-item-${it.id}`}>
                      <Trash2 className="w-3 h-3 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {order.items.length === 0 && <div className="text-sm text-muted-foreground py-4">{t("empty")}</div>}

            <div className="pt-3 space-y-1 font-mono text-sm">
              <div className="flex justify-between text-muted-foreground"><span>{t("subtotal")}</span><span>{money(order.subtotal)}</span></div>
              <div className="flex justify-between text-muted-foreground"><span>{t("tax")}</span><span>{money(order.tax)}</span></div>
              <div className="flex justify-between font-semibold text-base pt-2 border-t">
                <span>{t("total")}</span><span data-testid="order-total">{money(order.total)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick add */}
        {!paid && (
          <Card className="border shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-display">{t("add_item")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
                {items.filter((i) => i.available !== false).map((it) => (
                  <button
                    key={it.id}
                    onClick={() => addItem(it.id)}
                    data-testid={`quick-add-${it.id}`}
                    className="text-left p-3 rounded-md border hover:border-primary/60 hover:bg-muted/40 active:scale-95"
                  >
                    <div className="text-sm truncate">{localized(it.name)}</div>
                    <div className="text-xs font-mono text-primary mt-1">{money(it.price)}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Payment Dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">{t("pay")} — {money(order.total)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase text-muted-foreground mb-2">{lang === "tr" ? "Yöntem" : "Methode"}</div>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t("cash")}</SelectItem>
                  <SelectItem value="card">{t("card")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground mb-2">{t("tip")} (€)</div>
              <Input type="number" step="0.10" value={tip} onChange={(e) => setTip(e.target.value)} data-testid="pay-tip" className="font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>{t("cancel")}</Button>
            <Button onClick={pay} data-testid="confirm-pay">{t("pay")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
